/**
 * DMS Meta Pixel — centralised tracking layer.
 *
 * Design goals:
 *  1. Never break the app if Meta fails to load, is blocked, or is disabled.
 *  2. One canonical Pixel across the whole DMS ecosystem (root + app).
 *  3. Respect the existing consent banner (dms_cookie_consent = "all" | "essential").
 *     Marketing tracking only fires when consent === "all".
 *  4. All business events go through a small, stable API so future Meta CAPI
 *     wiring can be added without touching every call-site.
 *  5. Conversion events are protected against duplicate firing (React re-renders,
 *     success-page refresh, back/forward navigation, webhook retries, etc.)
 *     via a session-scoped `dedupeKey` + a generated `eventID` that Meta will
 *     accept for browser <-> CAPI deduplication in the future.
 *
 * Public API:
 *   initMetaPixel()                         // idempotent, called on app boot
 *   trackPageView(path)                     // SPA route change
 *   trackStandard(event, params)            // Meta standard events (Lead, InitiateCheckout, Purchase, ViewContent, CompleteRegistration)
 *   trackCustom(event, params)              // DMS_* custom events
 *   trackConversionOnce(key, fn)            // Idempotent wrapper for CompleteRegistration, Purchase, etc.
 *   onConsentChanged()                      // Re-init after user grants marketing consent
 */

const PIXEL_ID = (process.env.REACT_APP_META_PIXEL_ID || "").trim();
const ENABLED =
  PIXEL_ID.length > 0 &&
  (process.env.REACT_APP_META_PIXEL_ENABLED ?? "true").toLowerCase() !== "false";
const CONSENT_KEY = "dms_cookie_consent";
const DEDUPE_STORE = "dms_meta_dedupe_v1";
const DEDUPE_STORE_PERSISTENT = "dms_meta_dedupe_persistent_v1";

let pixelLoaded = false;
let pixelInitCalled = false;
let lastPageViewPath = null;

/* ---------------------------------------------------------------- Helpers */

function hasMarketingConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) === "all";
  } catch {
    return false;
  }
}

function loadDedupe(persistent = false) {
  const storage = persistent ? "localStorage" : "sessionStorage";
  const store = persistent ? DEDUPE_STORE_PERSISTENT : DEDUPE_STORE;
  try {
    return JSON.parse(window[storage].getItem(store) || "{}");
  } catch {
    return {};
  }
}

function saveDedupe(store, persistent = false) {
  const storage = persistent ? "localStorage" : "sessionStorage";
  const key = persistent ? DEDUPE_STORE_PERSISTENT : DEDUPE_STORE;
  try {
    window[storage].setItem(key, JSON.stringify(store));
  } catch { /* storage disabled — best-effort only */ }
}

function makeEventId() {
  // Compact non-cryptographic ID good enough for browser<->CAPI dedup.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function safeFbq(...args) {
  try {
    if (typeof window === "undefined") return;
    if (typeof window.fbq !== "function") return;
    window.fbq(...args);
  } catch (err) {
    // Never let tracking break the app.
    if (process.env.NODE_ENV !== "production") {
      console.debug("[metaPixel] fbq call failed", err);
    }
  }
}

/* ------------------------------------------------------------- Pixel boot */

function injectPixelScript() {
  if (pixelLoaded || typeof window === "undefined" || typeof document === "undefined") return;
  // Meta's canonical stub — vendored so we don't depend on the raw HTML snippet.
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  pixelLoaded = true;
}

export function initMetaPixel() {
  if (!ENABLED) return;
  if (pixelInitCalled) return;
  if (!hasMarketingConsent()) return;
  injectPixelScript();
  safeFbq("init", PIXEL_ID);
  pixelInitCalled = true;
  // Fire the initial PageView once, then rely on SPA route tracking after.
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  lastPageViewPath = path;
  safeFbq("track", "PageView");
}

/**
 * Re-check consent state. Call this after the CookieBanner sets consent.
 */
export function onConsentChanged() {
  if (!ENABLED) return;
  if (pixelInitCalled) return;
  if (hasMarketingConsent()) initMetaPixel();
}

/* ---------------------------------------------------------- Route tracking */

/**
 * Fire PageView for a meaningful client-side navigation.
 * De-duplicates against the last path so React re-renders don't double-count.
 */
export function trackPageView(path) {
  if (!ENABLED || !pixelInitCalled) return;
  if (!path) return;
  if (path === lastPageViewPath) return;
  lastPageViewPath = path;
  safeFbq("track", "PageView");
}

/* --------------------------------------------------- Event dispatch helpers */

export function trackStandard(event, params = {}) {
  if (!ENABLED || !pixelInitCalled) return;
  const eventId = params.eventID || makeEventId();
  const { eventID, ...rest } = params;
  safeFbq("track", event, rest, { eventID: eventId });
}

export function trackCustom(event, params = {}) {
  if (!ENABLED || !pixelInitCalled) return;
  const eventId = params.eventID || makeEventId();
  const { eventID, ...rest } = params;
  safeFbq("trackCustom", event, rest, { eventID: eventId });
}

/**
 * Fire an event AT MOST ONCE per browser session for a given dedupe key.
 *
 * Use for CompleteRegistration, DMS_OnboardingCompleted, DMS_StrategyGenerated,
 * DMS_CalendarGenerated, InitiateCheckout (per plan) and Purchase (per session_id).
 *
 * `key` should incorporate a stable business identifier (user_id, session_id, plan)
 * so that re-fires after tab reloads / component remounts don't double-count.
 *
 * Pass `persistent: true` to dedupe across browser sessions (localStorage) —
 * critical for user-scoped conversions like CompleteRegistration that must
 * never re-fire even after the tab is closed and reopened.
 */
export function trackConversionOnce(key, fireFn, { persistent = false } = {}) {
  if (!ENABLED || !pixelInitCalled) return false;
  if (!key || typeof fireFn !== "function") return false;
  const store = loadDedupe(persistent);
  if (store[key]) return false;
  store[key] = Date.now();
  saveDedupe(store, persistent);
  try {
    fireFn();
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[metaPixel] conversion firing failed", err);
    }
  }
  return true;
}

/* --------------------------------------------------- Convenience wrappers */

// These small helpers keep call-sites readable and self-documenting.
export const meta = {
  viewContent(contentName, extra = {}) {
    trackStandard("ViewContent", { content_name: contentName, ...extra });
  },
  appStarted(ctaLocation, sourcePage) {
    trackCustom("DMS_AppStarted", {
      cta_location: ctaLocation || "unknown",
      destination: "app",
      source_page: sourcePage || (typeof window !== "undefined" ? window.location.pathname : "/"),
    });
  },
  completeRegistration(userId) {
    // The deterministic event_id MUST match what the backend sends to Meta CAPI
    // in `meta_capi.send_complete_registration()` (dms_reg_{user_id}) so Meta
    // deduplicates browser + server hits.
    trackConversionOnce(`CompleteRegistration:${userId}`, () => {
      trackStandard("CompleteRegistration", {
        content_name: "DMS Account Registration",
        status: "completed",
        eventID: `dms_reg_${userId}`,
      });
    }, { persistent: true });
  },
  onboardingStarted(userId) {
    trackConversionOnce(`DMS_OnboardingStarted:${userId}`, () => {
      trackCustom("DMS_OnboardingStarted");
    }, { persistent: true });
  },
  onboardingCompleted(userId, extra = {}) {
    trackConversionOnce(`DMS_OnboardingCompleted:${userId}`, () => {
      trackCustom("DMS_OnboardingCompleted", extra);
    }, { persistent: true });
  },
  strategyGenerated(userId, extra = {}) {
    // Users can regenerate strategy — dedupe permanently per user so we only
    // count the first successful generation for the funnel. Regens are a
    // usage signal, not an acquisition milestone.
    trackConversionOnce(`DMS_StrategyGenerated:${userId}`, () => {
      trackCustom("DMS_StrategyGenerated", extra);
    }, { persistent: true });
  },
  calendarGenerated(userId, extra = {}) {
    trackConversionOnce(`DMS_CalendarGenerated:${userId}`, () => {
      trackCustom("DMS_CalendarGenerated", extra);
    }, { persistent: true });
  },
  planSelected(planName, value, currency) {
    trackCustom("DMS_PlanSelected", {
      plan_name: planName,
      ...(typeof value === "number" ? { value } : {}),
      ...(currency ? { currency } : {}),
    });
  },
  initiateCheckout(planName, value, currency) {
    // Session-only dedupe: fire once per plan per browser session so multi-click
    // on the "Start trial" button doesn't inflate volume, but the user picking
    // a different plan tomorrow still counts.
    trackConversionOnce(`InitiateCheckout:${planName}`, () => {
      trackStandard("InitiateCheckout", {
        content_name: "Dive Media Strategist Subscription",
        plan_name: planName,
        ...(typeof value === "number" ? { value } : {}),
        ...(currency ? { currency } : {}),
      });
    });
  },
  purchase({ sessionId, planName, value, currency }) {
    // The deterministic event_id MUST match what the backend sends to Meta
    // CAPI in `meta_capi.send_purchase()` (dms_purchase_{session_id}) so Meta
    // deduplicates browser + server hits — critical because Purchase fires
    // from BOTH the browser Pixel here AND the server (checkout-status +
    // Stripe webhook). Dedupe via localStorage covers refresh/back/forward.
    if (!sessionId) return;
    trackConversionOnce(`Purchase:${sessionId}`, () => {
      trackStandard("Purchase", {
        content_name: "Dive Media Strategist Subscription",
        plan_name: planName || "unknown",
        ...(typeof value === "number" ? { value } : {}),
        ...(currency ? { currency } : {}),
        eventID: `dms_purchase_${sessionId}`,
      });
    }, { persistent: true });
  },
  contentGenerated(contentType, channel) {
    trackCustom("DMS_ContentGenerated", {
      content_type: contentType || "unknown",
      ...(channel ? { channel } : {}),
    });
  },
  channelConnected(channel) {
    trackCustom("DMS_ChannelConnected", { channel: channel || "unknown" });
  },
};

export const META_PIXEL_ID = PIXEL_ID;
